import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import {EmployeeRoleLevel, RedactedWorkstreamMetric, WorkstreamMetric} from 'client';
import {CURRENT_QUARTER, CURRENT_YEAR} from 'constant';
import {t} from 'i18next';
import {useCallback, useMemo, useState} from 'react';
import {useWorkstreamMetricMutation} from 'views/workstreams/hooks';
import {useWorkstreamJiraElevations} from 'views/workstreams/jiraObjects';
import {
  getFullWorkstreamMetrics,
  getWorkstreamDri,
  isFullWorkstreamMetric,
  isWorkstreamStatusArchived,
  WorkstreamWithHiddenMnpiMetricsCount,
} from 'views/workstreams/utils';

import type {MetricsGrain} from './MetricsGrainControls';
import {MetricsTimeframeSelector} from './MetricsTimeframeSelector';
import styles from './WorkstreamDetailsShell.module.css';
import {WorkstreamManageOutcomesTrigger} from './WorkstreamManageOutcomesModal';
import {logWorkstreamMetricsTimeframeChanged} from './workstreamMetricAnalytics';
import {WorkstreamMetricsAnnualGroupedTable} from './WorkstreamMetricsAnnualTable';
import {
  getDefaultMetricQuarter,
  type MetricQuarter,
  WorkstreamMetricsQuarterGroupedTable,
} from './WorkstreamMetricsQuarterTable';
import {RedesignMetricsList} from './WorkstreamRedesignSections';
import {WorkstreamSectionCard} from './WorkstreamSectionCard';

export const WorkstreamMetricsSection = ({
  workstream,
}: {
  workstream: WorkstreamWithHiddenMnpiMetricsCount;
}) => {
  const hiddenMnpiMetricsCount = workstream.hidden_mnpi_metrics_count ?? 0;
  const compatibleMetrics = workstream.metrics as Array<
    WorkstreamMetric | RedactedWorkstreamMetric
  >;
  const metrics = useMemo(
    () => compatibleMetrics.filter((metric) => metric.type === 'metric'),
    [compatibleMetrics]
  );
  const editableMetrics = useMemo(
    () => getFullWorkstreamMetrics(compatibleMetrics),
    [compatibleMetrics]
  );
  const editableMetricRows = useMemo(
    () => editableMetrics.filter((metric) => metric.type === 'metric'),
    [editableMetrics]
  );
  const hasRedactedMetrics = metrics.some((metric) => !isFullWorkstreamMetric(metric));
  const subWorkstreamMetricGroups = useMemo(
    () =>
      [...(workstream.dependencies ?? [])]
        .filter(({dependent}) => !dependent.archived && !isWorkstreamStatusArchived(dependent))
        .sort((a, b) => a.order - b.order)
        .map((dependency) => ({
          id: dependency.dependent.id,
          name: dependency.dependent.name,
          metrics: (
            dependency.dependent.metrics as Array<WorkstreamMetric | RedactedWorkstreamMetric>
          ).filter((metric) => metric.type === 'metric'),
        }))
        .filter((group) => group.metrics.length > 0),
    [workstream.dependencies]
  );
  const {
    jiraObjects: resolvedJiraObjects,
    loadElevatableJiraOutcomes,
    elevateJiraOutcome,
    detachJiraOutcome,
  } = useWorkstreamJiraElevations(workstream, true, {refresh: true});
  const workstreamOwner = getWorkstreamDri(workstream) as EmployeeRoleLevel | undefined;
  const hasAnyMetrics = metrics.length > 0 || subWorkstreamMetricGroups.length > 0;
  // Metrics view: an Annual/Quarterly grain toggle, defaulting to Quarterly.
  const [grain, setGrain] = useState<MetricsGrain>('quarterly');
  const [quarter, setQuarter] = useState<MetricQuarter>(() =>
    getDefaultMetricQuarter(CURRENT_QUARTER)
  );
  // The Annual grain's year dropdown offers a fixed FY26–FY27 window, so the year
  // follows the selection directly.
  const [selectedMetricsYear, setSelectedMetricsYear] = useState(CURRENT_YEAR);
  const [subWorkstreamsExpanded, setSubWorkstreamsExpanded] = useState(false);
  const selectedPeriod = `${selectedMetricsYear}Q${CURRENT_QUARTER}`;
  const selectedForecastType = 'target';
  const visibleSubGroups = subWorkstreamMetricGroups;
  const hasParentMetrics = metrics.length > 0;
  const showQuarterTable =
    grain === 'quarterly' && (metrics.length > 0 || visibleSubGroups.length > 0);
  const {createMetric, updateMetric, deleteMetric, attachMetric, isPending} =
    useWorkstreamMetricMutation();

  const handleMetricChange = useCallback(
    async (
      metricId: number,
      metricUpdate: Partial<WorkstreamMetric>,
      reason?: string,
      skipAuditLogOrExistingMetric?: boolean | WorkstreamMetric,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Existing issue; this suppression was only added to allow existing lint warnings to be upgraded to errors.
      _disableSnackbar?: boolean
    ) => {
      if (metricId === -1) {
        return createMetric(
          workstream.id,
          metricUpdate,
          editableMetrics.length + 1,
          reason ?? undefined
        );
      }

      const skipAuditLog =
        typeof skipAuditLogOrExistingMetric === 'boolean' ? skipAuditLogOrExistingMetric : false;
      const existingMetric =
        (typeof skipAuditLogOrExistingMetric === 'object' ? skipAuditLogOrExistingMetric : null) ??
        editableMetrics.find((metric) => metric.id === metricId);
      if (!existingMetric) {
        return;
      }

      await updateMetric(
        workstream.id,
        metricId,
        existingMetric,
        metricUpdate,
        reason ?? undefined,
        skipAuditLog
      );
    },
    [createMetric, editableMetrics, updateMetric, workstream.id]
  );
  const handleDeleteMetric = useCallback(
    async (metric: WorkstreamMetric, reason: string) => {
      await deleteMetric(
        {
          workstreamId: workstream.id,
          metricId: metric.id,
        },
        reason ?? undefined
      );
    },
    [deleteMetric, workstream.id]
  );
  const handleAttachMetric = useCallback(
    async (attachRequest: {metric_id: number; order?: number | null; is_primary?: boolean}) => {
      return attachMetric(workstream.id, attachRequest);
    },
    [attachMetric, workstream.id]
  );
  const handleMetricsTimeframeSelect = ({
    grain: nextGrain,
    quarter: nextQuarter,
    year: nextYear,
  }: {
    grain: MetricsGrain;
    quarter: MetricQuarter;
    year: number;
  }) => {
    if (nextGrain !== grain) {
      logWorkstreamMetricsTimeframeChanged('details', {
        change_type: 'grain',
        grain: nextGrain,
        quarter: nextGrain === 'quarterly' ? nextQuarter : undefined,
        year: nextYear,
        workstream_id: workstream.id,
      });
    }

    const changed =
      nextYear !== selectedMetricsYear || (nextGrain === 'quarterly' && nextQuarter !== quarter);
    if (changed) {
      logWorkstreamMetricsTimeframeChanged('details', {
        change_type: 'timeframe',
        grain: nextGrain,
        quarter: nextGrain === 'quarterly' ? nextQuarter : undefined,
        year: nextYear,
        workstream_id: workstream.id,
      });
    }

    setGrain(nextGrain);
    setQuarter(nextQuarter);
    setSelectedMetricsYear(nextYear);
  };

  return (
    <WorkstreamSectionCard
      title={t('metrics')}
      action={
        hasAnyMetrics ? (
          <Box display="flex" alignItems="center" style={{gap: 8}}>
            <MetricsTimeframeSelector
              grain={grain}
              quarter={quarter}
              year={selectedMetricsYear}
              onSelect={handleMetricsTimeframeSelect}
            />
          </Box>
        ) : undefined
      }
      titleAction={
        <WorkstreamManageOutcomesTrigger
          workstreamId={workstream.id}
          workstreamName={workstream.name}
          metrics={editableMetrics}
          jiraOutcomes={resolvedJiraObjects}
          initialTab="metrics"
          autoAddRowOnOpen={!editableMetricRows.length}
          isPending={isPending}
          workstreamOwner={workstreamOwner}
          onSaveMetric={handleMetricChange}
          onAttachMetric={handleAttachMetric}
          onDeleteMetric={handleDeleteMetric}
          loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
          onElevateJiraOutcome={elevateJiraOutcome}
          onDetachJiraOutcome={detachJiraOutcome}
          metricPeriodOptions={[selectedPeriod]}
          triggerVariant="icon"
          triggerLabel={t('edit_metrics').toString()}
          hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
        />
      }
    >
      <>
        {visibleSubGroups.length > 0 && !hasParentMetrics ? (
          <Box marginBottom="16">
            <WorkstreamManageOutcomesTrigger
              workstreamId={workstream.id}
              workstreamName={workstream.name}
              metrics={editableMetrics}
              jiraOutcomes={resolvedJiraObjects}
              initialTab="metrics"
              autoAddRowOnOpen
              isPending={isPending}
              workstreamOwner={workstreamOwner}
              onSaveMetric={handleMetricChange}
              onAttachMetric={handleAttachMetric}
              onDeleteMetric={handleDeleteMetric}
              loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
              onElevateJiraOutcome={elevateJiraOutcome}
              onDetachJiraOutcome={detachJiraOutcome}
              metricPeriodOptions={[selectedPeriod]}
              triggerVariant="chip"
              triggerLabel={t('add_metric').toString()}
              hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
            />
          </Box>
        ) : null}
        <div
          className={`${styles.updateSectionCard} ${styles.updateSectionCardFlush} ${
            !hasAnyMetrics ? styles.updateSectionCardBorderless : ''
          }`}
        >
          {showQuarterTable ? (
            // Quarterly: render the parent metrics and any sub-workstream groups in
            // one shared table so the column header is drawn once (matching the
            // list/pillar scorecard layout) instead of repeating per group.
            <WorkstreamMetricsQuarterGroupedTable
              metrics={metrics}
              subGroups={visibleSubGroups}
              subWorkstreamsExpanded={subWorkstreamsExpanded}
              onSubWorkstreamsExpandedChange={setSubWorkstreamsExpanded}
              year={selectedMetricsYear}
              quarter={quarter}
              handleMetricChange={handleMetricChange}
              owner={workstreamOwner}
              statusChangeSource="details_inline_quarterly"
              workstreamId={workstream.id}
            />
          ) : (
            <>
              {metrics.length > 0 || visibleSubGroups.length > 0 ? (
                <WorkstreamMetricsAnnualGroupedTable
                  metrics={metrics}
                  subGroups={visibleSubGroups
                    .map((group) => ({...group, metrics: group.metrics}))
                    .filter((group) => group.metrics.length > 0)}
                  subWorkstreamsExpanded={subWorkstreamsExpanded}
                  onSubWorkstreamsExpandedChange={setSubWorkstreamsExpanded}
                  year={selectedMetricsYear}
                  handleMetricChange={handleMetricChange}
                  owner={workstreamOwner}
                  statusChangeSource="details_inline_annual"
                  workstreamId={workstream.id}
                />
              ) : (
                <RedesignMetricsList
                  workstreamId={workstream.id}
                  workstreamName={workstream.name}
                  metrics={getFullWorkstreamMetrics(metrics)}
                  allEditableMetrics={editableMetrics}
                  jiraOutcomes={resolvedJiraObjects}
                  owner={getWorkstreamDri(workstream)}
                  selectedPeriod={selectedPeriod}
                  selectedForecastType={selectedForecastType}
                  variant="details"
                  metricPeriodOptions={[selectedPeriod]}
                  handleMetricChange={handleMetricChange}
                  statusChangeSource="details_inline_annual"
                  onAttachMetric={handleAttachMetric}
                  onDelete={handleDeleteMetric}
                  loadElevatableJiraOutcomes={loadElevatableJiraOutcomes}
                  onElevateJiraOutcome={elevateJiraOutcome}
                  onDetachJiraOutcome={detachJiraOutcome}
                  isPending={isPending}
                  showBaselineColumn
                  hiddenMnpiMetricsCount={hiddenMnpiMetricsCount}
                />
              )}
            </>
          )}
        </div>
        {hiddenMnpiMetricsCount > 0 && (!hasRedactedMetrics || grain !== 'quarterly') && (
          <Box marginTop="8" marginLeft="8">
            <Text size="small" color="subtle">
              {t('hidden_mnpi_metrics_count', {count: hiddenMnpiMetricsCount})}
            </Text>
          </Box>
        )}
      </>
    </WorkstreamSectionCard>
  );
};
